import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MgmtLayout from '@/components/feature/MgmtLayout';
import { supabase } from '@/lib/supabase';
import PaymentsTab from '@/pages/management/projects/components/PaymentsTab';
import { getDefaultCoverUrl, getProjectCoverImage } from '@/lib/projectCover';

type ProjectType = 'Development' | 'Renovation';

interface StageRow {
  id: string;
  stage_name: string;
  sort_order: number;
  status: string;
  planned_start_date: string | null;
  planned_end_date: string | null;
  actual_start_date: string | null;
  actual_end_date: string | null;
  delay_reason: string | null;
  completion_percent: number;
}

interface FormState {
  project_name: string;
  site_address: string;
  client_name: string;
  summary: string;
  project_type: ProjectType;
  start_date: string;
  target_completion_date: string;
  cover_image: string;
}

// STAGE_STATUS_OPTIONS is now built inside the component using t()

const DEFAULT_STAGE_NAMES = ['Demolition', 'Structure', 'Rough-in', 'Fit-out', 'Finishing', 'Handover'];

export default function EditProject() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const STAGE_STATUS_OPTIONS = [
    { value: 'not_started', label: t('stages.statusNotStarted') },
    { value: 'in_progress', label: t('stages.statusInProgress') },
    { value: 'completed', label: t('stages.statusCompleted') },
    { value: 'delayed', label: t('stages.statusDelayed') },
    { value: 'on_hold', label: t('status.onHold') },
  ];

  const TABS = [
    t('editProject.basicInfo'),
    t('editProject.typeCover'),
    t('editProject.stages'),
    t('editProject.schedule'),
    t('editProject.payments'),
  ];
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [form, setForm] = useState<FormState>({
    project_name: '',
    site_address: '',
    client_name: '',
    summary: '',
    project_type: 'Renovation',
    start_date: '',
    target_completion_date: '',
    cover_image: '',
  });

  const [stages, setStages] = useState<StageRow[]>([]);
  const [newStageName, setNewStageName] = useState('');
  const [addingStage, setAddingStage] = useState(false);

  useEffect(() => {
    if (id) loadProject();
  }, [id]);

  async function loadProject() {
    setLoading(true);
    const { data: project, error } = await supabase
      .from('jphph_projects')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error || !project) {
      setSaveError(t('editProject.projectNotFound'));
      setLoading(false);
      return;
    }

    setForm({
      project_name: project.project_name || '',
      site_address: project.site_address || '',
      client_name: project.client_name || '',
      summary: project.summary || '',
      project_type: (project.project_type as ProjectType) || 'Renovation',
      start_date: project.start_date || '',
      target_completion_date: project.target_completion_date || '',
      cover_image: project.cover_image || '',
    });

    const { data: stageRows } = await supabase
      .from('jphph_project_stages')
      .select('*')
      .eq('project_id', id)
      .order('sort_order', { ascending: true });

    setStages(stageRows || []);
    setLoading(false);
  }

  const update = (field: keyof FormState, value: string) =>
    setForm((f) => ({ ...f, [field]: value }));

  const updateStage = (stageId: string, field: keyof StageRow, value: string | number) => {
    setStages((prev) =>
      prev.map((s) => (s.id === stageId ? { ...s, [field]: value } : s))
    );
  };

  async function addStage() {
    if (!newStageName.trim() || !id) return;
    setAddingStage(true);
    const maxOrder = stages.length > 0 ? Math.max(...stages.map((s) => s.sort_order)) : 0;
    const { data: newStage, error } = await supabase
      .from('jphph_project_stages')
      .insert({
        project_id: id,
        stage_name: newStageName.trim(),
        sort_order: maxOrder + 1,
        status: 'not_started',
        completion_percent: 0,
      })
      .select()
      .maybeSingle();

    if (!error && newStage) {
      setStages((prev) => [...prev, newStage as StageRow]);
      setNewStageName('');
    }
    setAddingStage(false);
  }

  async function handleSave() {
    if (!id) return;
    setSaving(true);
    setSaveError('');
    setSaveSuccess(false);

    // Preserve existing custom cover if user didn't clear it
    const coverImage = form.cover_image || getDefaultCoverUrl(form.project_type);

    // Update project basic info
    const { error: projectErr } = await supabase
      .from('jphph_projects')
      .update({
        project_name: form.project_name.trim(),
        site_address: form.site_address.trim() || null,
        client_name: form.client_name.trim() || null,
        summary: form.summary.trim() || null,
        project_type: form.project_type,
        start_date: form.start_date || null,
        target_completion_date: form.target_completion_date || null,
        cover_image: coverImage,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id);

    if (projectErr) {
      setSaveError(`Failed to save project: ${projectErr.message}`);
      setSaving(false);
      return;
    }

    // Update each stage
    for (const stage of stages) {
      await supabase
        .from('jphph_project_stages')
        .update({
          stage_name: stage.stage_name,
          sort_order: stage.sort_order,
          status: stage.status,
          planned_start_date: stage.planned_start_date || null,
          planned_end_date: stage.planned_end_date || null,
          actual_start_date: stage.actual_start_date || null,
          actual_end_date: stage.actual_end_date || null,
          delay_reason: stage.delay_reason || null,
          completion_percent: stage.completion_percent || 0,
          updated_at: new Date().toISOString(),
        })
        .eq('id', stage.id);
    }

    setSaving(false);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  }

  if (loading) {
    return (
      <MgmtLayout title={t('editProject.title')} showBack backTo={`/management/projects/${id}`}>
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-[#E8621A] text-3xl"></i>
        </div>
      </MgmtLayout>
    );
  }

  return (
    <MgmtLayout title={t('editProject.title')} showBack backTo={`/management/projects/${id}`}>
      <div className="px-4 pt-4 pb-10 max-w-2xl mx-auto">

        {/* Tab bar */}
        <div className="flex gap-1 bg-[#F5F5F3] rounded-xl p-1 mb-6">
          {TABS.map((tab, i) => (
            <button
              key={tab}
              onClick={() => setActiveTab(i)}
              className={`flex-1 py-2 rounded-lg text-xs font-semibold cursor-pointer whitespace-normal sm:whitespace-nowrap transition-colors ${
                activeTab === i ? 'bg-white text-[#E8621A]' : 'text-[#9CA3AF]'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* ── Tab 0: Basic Info ── */}
        {activeTab === 0 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-1.5">
                {t('editProject.projectName')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.project_name}
                onChange={(e) => update('project_name', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:border-[#E8621A] transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-1.5">{t('editProject.siteAddress')}</label>
              <input
                type="text"
                value={form.site_address}
                onChange={(e) => update('site_address', e.target.value)}
                placeholder="e.g. 42 Bethells Rd, Henderson, Auckland"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:border-[#E8621A] transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-1.5">{t('editProject.clientName')}</label>
              <input
                type="text"
                value={form.client_name}
                onChange={(e) => update('client_name', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:border-[#E8621A] transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-1.5">{t('editProject.projectDescription')}</label>
              <textarea
                value={form.summary}
                onChange={(e) => update('summary', e.target.value)}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:border-[#E8621A] transition-colors resize-none"
              />
              <p className="text-[10px] text-[#9CA3AF] mt-1 text-right">{form.summary.length}/500</p>
            </div>
          </div>
        )}

        {/* ── Tab 1: Type & Cover ── */}
        {activeTab === 1 && (
          <div className="space-y-5">
            <div>
              <h3 className="text-sm font-bold text-[#1A1A1A] mb-3">{t('editProject.projectType')}</h3>
              <div className="grid grid-cols-2 gap-3">
                {(['Development', 'Renovation'] as ProjectType[]).map((type) => (
                  <button
                    key={type}
                    onClick={() => update('project_type', type)}
                    className={`relative flex flex-col items-start p-4 rounded-2xl border-2 text-left cursor-pointer transition-all ${
                      form.project_type === type
                        ? 'border-[#E8621A] bg-[#FEF0E7]'
                        : 'border-gray-200 bg-white hover:border-[#E8621A]/40'
                    }`}
                  >
                    {form.project_type === type && (
                      <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-[#E8621A] flex items-center justify-center">
                        <i className="ri-check-line text-white text-xs"></i>
                      </div>
                    )}
                    <div className="w-10 h-10 rounded-xl bg-[#FEF0E7] flex items-center justify-center mb-2">
                      <i className={`${type === 'Development' ? 'ri-building-4-line' : 'ri-hammer-line'} text-[#E8621A] text-xl`}></i>
                    </div>
                    <div className="font-bold text-[#1A1A1A] text-sm">{type}</div>
                    <div className="text-[#6B7280] text-xs mt-1 leading-snug">
                      {type === 'Development' ? t('newProject.developmentDesc') : t('newProject.renovationDesc')}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-bold text-[#1A1A1A] mb-2">{t('editProject.coverPhoto')}</h3>
              <p className="text-xs text-[#9CA3AF] mb-3">
                Paste an image URL, or leave blank to use the default cover for this project type.
              </p>

              {/* Preview */}
              <div className="h-40 rounded-xl overflow-hidden mb-3 bg-gray-100">
                <img
                  src={getProjectCoverImage(form)}
                  alt="Cover preview"
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <input
                type="url"
                value={form.cover_image}
                onChange={(e) => update('cover_image', e.target.value)}
                placeholder={t('editProject.coverUrlPlaceholder')}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:border-[#E8621A] transition-colors"
              />

              {form.cover_image && (
                <button
                  onClick={() => update('cover_image', '')}
                  className="mt-2 text-xs text-[#9CA3AF] cursor-pointer hover:text-[#E8621A] transition-colors"
                >
                  {t('editProject.resetToDefault')}
                </button>
              )}

              <div className="mt-3 bg-[#F9FAFB] rounded-xl p-3 flex items-start gap-2">
                <i className="ri-information-line text-[#9CA3AF] text-sm mt-0.5"></i>
                <p className="text-[#6B7280] text-xs leading-relaxed">
                  Default cover: <strong>Development</strong> projects use the dev.webp default.
                  <strong> Renovation</strong> projects use the reno.webp default.
                  Changing the project type will update the default cover automatically unless a custom photo is set.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── Tab 2: Stages ── */}
        {activeTab === 2 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm font-bold text-[#1A1A1A]">{t('editProject.constructionStages')}</h3>
              <span className="text-xs text-[#9CA3AF]">{stages.length} {t('tabs.stages').toLowerCase()}</span>
            </div>

            {stages.map((stage, idx) => (
              <div key={stage.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3 bg-[#F9FAFB] border-b border-gray-100">
                  <span className="w-6 h-6 rounded-full bg-[#E8621A] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                    {idx + 1}
                  </span>
                  <input
                    type="text"
                    value={stage.stage_name}
                    onChange={(e) => updateStage(stage.id, 'stage_name', e.target.value)}
                    className="flex-1 text-sm font-bold text-[#1A1A1A] bg-transparent focus:outline-none"
                  />
                  <select
                    value={stage.status}
                    onChange={(e) => updateStage(stage.id, 'status', e.target.value)}
                    className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white focus:outline-none focus:border-[#E8621A] cursor-pointer max-w-[140px] sm:max-w-none"
                  >
                    {STAGE_STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className="p-4 space-y-3">
                  {/* Completion */}
                  <div>
                    <label className="block text-xs text-[#374151] mb-1">
                      {t('editProject.completion')}: <strong>{stage.completion_percent}%</strong>
                    </label>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={5}
                      value={stage.completion_percent}
                      onChange={(e) => updateStage(stage.id, 'completion_percent', parseInt(e.target.value))}
                      className="w-full accent-[#E8621A]"
                    />
                  </div>

                  {/* Planned dates */}
                  <div>
                    <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-2">{t('editProject.planned')}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-[#374151] mb-1">{t('editProject.start')}</label>
                        <input
                          type="date"
                          value={stage.planned_start_date || ''}
                          onChange={(e) => updateStage(stage.id, 'planned_start_date', e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs bg-white focus:outline-none focus:border-[#E8621A]"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-[#374151] mb-1">{t('editProject.end')}</label>
                        <input
                          type="date"
                          value={stage.planned_end_date || ''}
                          onChange={(e) => updateStage(stage.id, 'planned_end_date', e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs bg-white focus:outline-none focus:border-[#E8621A]"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Actual dates */}
                  <div>
                    <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-2">{t('editProject.actual')}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-[#374151] mb-1">{t('editProject.start')}</label>
                        <input
                          type="date"
                          value={stage.actual_start_date || ''}
                          onChange={(e) => updateStage(stage.id, 'actual_start_date', e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs bg-white focus:outline-none focus:border-[#E8621A]"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-[#374151] mb-1">{t('editProject.end')}</label>
                        <input
                          type="date"
                          value={stage.actual_end_date || ''}
                          onChange={(e) => updateStage(stage.id, 'actual_end_date', e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs bg-white focus:outline-none focus:border-[#E8621A]"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Delay reason */}
                  {stage.status === 'delayed' && (
                    <div>
                      <label className="block text-xs font-semibold text-red-500 mb-1">{t('editProject.delayReason')}</label>
                      <textarea
                        value={stage.delay_reason || ''}
                        onChange={(e) => updateStage(stage.id, 'delay_reason', e.target.value)}
                        placeholder={t('editProject.delayReasonPlaceholder')}
                        rows={2}
                        maxLength={500}
                        className="w-full px-3 py-2 rounded-lg border border-red-200 text-xs bg-white focus:outline-none focus:border-red-400 resize-none"
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Add stage */}
            <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-4">
              <p className="text-xs font-semibold text-[#374151] mb-2">{t('editProject.addNewStage')}</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newStageName}
                  onChange={(e) => setNewStageName(e.target.value)}
                  placeholder={t('editProject.stageName')}
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:border-[#E8621A]"
                  onKeyDown={(e) => { if (e.key === 'Enter') addStage(); }}
                />
                <button
                  onClick={addStage}
                  disabled={!newStageName.trim() || addingStage}
                  className="px-4 py-2 rounded-lg bg-[#E8621A] text-white text-sm font-semibold cursor-pointer whitespace-normal sm:whitespace-nowrap disabled:opacity-50"
                >
                  {t('editProject.add')}
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {DEFAULT_STAGE_NAMES.filter((n) => !stages.some((s) => s.stage_name === n)).map((n) => (
                  <button
                    key={n}
                    onClick={() => setNewStageName(n)}
                    className="text-[10px] px-2 py-0.5 rounded-full bg-[#F9FAFB] border border-gray-200 text-[#6B7280] cursor-pointer hover:border-[#E8621A] hover:text-[#E8621A] transition-colors whitespace-nowrap"
                  >
                    + {n}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Tab 3: Schedule ── */}
        {activeTab === 3 && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
              <h3 className="text-sm font-bold text-[#1A1A1A]">{t('editProject.overallSchedule')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#374151] mb-1.5">{t('editProject.startDate')}</label>
                  <input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => update('start_date', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:border-[#E8621A]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#374151] mb-1.5">{t('editProject.estCompletion')}</label>
                  <input
                    type="date"
                    value={form.target_completion_date}
                    onChange={(e) => update('target_completion_date', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:border-[#E8621A]"
                  />
                </div>
              </div>
              {form.start_date && form.target_completion_date && (
                <div className="bg-[#F9FAFB] rounded-xl p-3 flex items-center gap-2">
                  <i className="ri-time-line text-[#E8621A] text-sm"></i>
                  <span className="text-sm text-[#374151] font-medium">
                    {(() => {
                      const diffDays = Math.round(
                        (new Date(form.target_completion_date).getTime() - new Date(form.start_date).getTime()) / 86400000
                      );
                      const months = Math.floor(diffDays / 30);
                      const weeks = Math.floor(diffDays / 7);
                      if (months >= 2) return t('newProject.monthsDuration', { n: months });
                      if (weeks >= 1) return weeks === 1 ? t('newProject.weekDuration', { n: weeks }) : t('newProject.weeksDuration', { n: weeks });
                      return diffDays === 1 ? t('newProject.dayDuration', { n: diffDays }) : t('newProject.daysDuration', { n: diffDays });
                    })()}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Tab 4: Payments ── */}
        {activeTab === 4 && id && (
          <div>
            <div className="bg-[#F9FAFB] rounded-xl p-3 mb-4 flex items-start gap-2">
              <i className="ri-bank-card-line text-[#E8621A] text-sm mt-0.5"></i>
              <p className="text-[#6B7280] text-xs leading-relaxed">
                Manage the payment schedule for this project. Clients can view this and notify you when payments are made.
              </p>
            </div>
            <PaymentsTab projectId={id} />
          </div>
        )}

        {/* Error / Success */}
        {saveError && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
            <i className="ri-error-warning-line text-red-500 text-sm mt-0.5"></i>
            <p className="text-red-600 text-sm">{saveError}</p>
          </div>
        )}
        {saveSuccess && (
          <div className="mt-4 bg-[#F0FDF4] border border-[#BBF7D0] rounded-xl p-3 flex items-center gap-2">
            <i className="ri-check-double-line text-[#059669] text-sm"></i>
            <p className="text-[#059669] text-sm font-semibold">{t('editProject.changesSaved')}</p>
          </div>
        )}

        {/* Save button — hidden on Payments tab */}
        {activeTab !== 4 && (
          <button
            onClick={handleSave}
            disabled={saving || !form.project_name.trim()}
            className="w-full mt-6 py-4 rounded-xl bg-[#E8621A] text-white font-bold text-sm cursor-pointer whitespace-normal sm:whitespace-nowrap disabled:opacity-50 hover:bg-[#B84C10] transition-colors flex items-center justify-center gap-2"
          >
            {saving ? (
              <><i className="ri-loader-4-line animate-spin"></i>{t('editProject.savingChanges')}</>
            ) : (
              <><i className="ri-save-line"></i>{t('editProject.saveChanges')}</>
            )}
          </button>
        )}
      </div>
    </MgmtLayout>
  );
}